import { supabase } from '../config/supabase'
import { BACKLINK_CATEGORIES } from '../config/api'

export class AnalyticsService {
  static async getUserAnalytics(userId) {
    try {
      console.log('Loading analytics for user:', userId)
      
      // Default empty data structure
      const defaultData = {
        totalSearches: 0,
        totalOpportunities: 0,
        categoryBreakdown: Object.values(BACKLINK_CATEGORIES).reduce((acc, category) => {
          acc[category] = 0
          return acc
        }, {}),
        statusBreakdown: {
          new: 0,
          submitted: 0,
          live: 0
        },
        recentSearches: [],
        searchTrend: [],
        relatedKeywords: [],
        timeSaved: 0
      }

      // Get all user searches
      const { data: searches, error: searchError } = await supabase
        .from('searches')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
      
      console.log('Searches query result:', { searches, searchError })
      
      if (searchError) {
        console.error('Search error:', searchError)
        throw searchError
      }
      
      if (!searches || searches.length === 0) {
        console.log('No searches found, returning default data')
        return defaultData
      }
      
      // Calculate total searches
      const totalSearches = searches.length
      console.log('Total searches:', totalSearches)
      
      // Get recent searches (last 5)
      const recentSearches = searches.slice(0, 5)
      
      // Process all opportunities and calculate stats
      let totalOpportunities = 0
      const categoryBreakdown = { ...defaultData.categoryBreakdown }
      
      // Collect all keywords for related keyword generation
      const allKeywords = searches.map(search => search.keyword).filter(keyword => keyword)
      
      // Process search results for each search
      searches.forEach(search => {
        if (search.results && Array.isArray(search.results)) {
          totalOpportunities += search.results.length
          
          // Process categories
          search.results.forEach(result => {
            const category = result.category || 'Other'
            categoryBreakdown[category] = (categoryBreakdown[category] || 0) + 1
          })
        }
      })
      
      console.log('Processed opportunities:', { totalOpportunities, categoryBreakdown })
      
      // Get all backlink statuses
      const { data: statuses, error: statusError } = await supabase
        .from('backlink_status_rtk7309')
        .select('status, result_id, search_id')
        .eq('user_id', userId)
      
      console.log('Statuses query result:', { statuses, statusError })
      
      if (statusError) {
        console.error('Status error:', statusError)
        // Don't throw error, just use empty statuses
      }
      
      // Calculate status breakdown
      const statusBreakdown = {
        new: 0,
        submitted: 0,
        live: 0
      }
      
      // Track status by category for time saved calculation
      const statusByCategory = {
        'Blog Comments': { submitted: 0, live: 0 },
        'Guest Posts': { submitted: 0, live: 0 },
        'Forum Comments': { submitted: 0, live: 0 },
        'Resource Pages': { submitted: 0, live: 0 },
        'Other': { submitted: 0, live: 0 }
      }
      
      if (statuses && statuses.length > 0) {
        // First count by status
        statuses.forEach(item => {
          if (statusBreakdown.hasOwnProperty(item.status)) {
            statusBreakdown[item.status]++
          }
        })
        
        // Now collect category information for submitted/live statuses
        for (const status of statuses) {
          if (status.status === 'submitted' || status.status === 'live') {
            // Find the search that contains this result
            for (const search of searches) {
              if (search.id === status.search_id && search.results && Array.isArray(search.results)) {
                // Find the specific result
                const result = search.results.find(r => String(r.id) === String(status.result_id))
                if (result) {
                  const category = result.category || 'Other'
                  if (statusByCategory[category]) {
                    statusByCategory[category][status.status]++
                  }
                  break // Found the result, no need to check other searches
                }
              }
            }
          }
        }
      }
      
      console.log('Processed statuses:', { statusBreakdown, statusByCategory })
      
      // Calculate search trend (last 7 days)
      const searchTrend = await this.calculateSearchTrend(userId)
      
      // Generate related keywords
      const relatedKeywords = await this.generateRelatedKeywords(allKeywords)
      
      // Calculate time saved based on the updated formula:
      // (Total searches * 10 minutes) + (Submitted Backlinks * 20 minutes) + (Live Backlinks * 20 minutes)
      let timeInMinutes = 0
      
      // Time for searches (10 minutes each)
      timeInMinutes += totalSearches * 10
      
      // Time for submitted backlinks (20 minutes each)
      timeInMinutes += statusBreakdown.submitted * 20
      
      // Time for live backlinks (20 minutes each)
      timeInMinutes += statusBreakdown.live * 20
      
      // Convert to hours with one decimal point
      const timeSaved = Math.round((timeInMinutes / 60) * 10) / 10
      
      const finalData = {
        totalSearches,
        totalOpportunities,
        categoryBreakdown,
        statusBreakdown,
        recentSearches,
        searchTrend,
        relatedKeywords,
        timeSaved
      }
      
      console.log('Final analytics data:', finalData)
      return finalData
    } catch (error) {
      console.error('Error generating analytics:', error)
      // Return default data instead of throwing
      return {
        totalSearches: 0,
        totalOpportunities: 0,
        categoryBreakdown: Object.values(BACKLINK_CATEGORIES).reduce((acc, category) => {
          acc[category] = 0
          return acc
        }, {}),
        statusBreakdown: {
          new: 0,
          submitted: 0,
          live: 0
        },
        recentSearches: [],
        searchTrend: Array(7).fill().map((_, i) => ({
          date: new Date(new Date().setDate(new Date().getDate() - (6 - i))).toISOString(),
          count: 0
        })),
        relatedKeywords: [],
        timeSaved: 0
      }
    }
  }

  static async calculateSearchTrend(userId) {
    try {
      // Get the current date and 7 days ago
      const endDate = new Date()
      const startDate = new Date()
      startDate.setDate(startDate.getDate() - 6) // Last 7 days including today
      
      // Format dates for Supabase query
      const startDateStr = startDate.toISOString()
      const endDateStr = endDate.toISOString()
      
      // Get searches in date range
      const { data: searches, error } = await supabase
        .from('searches')
        .select('created_at')
        .eq('user_id', userId)
        .gte('created_at', startDateStr)
        .lte('created_at', endDateStr)
      
      if (error) {
        console.error('Error in calculateSearchTrend:', error)
        throw error
      }
      
      // Initialize array with zeros for all 7 days
      const dateArray = []
      for (let i = 0; i < 7; i++) {
        const date = new Date(startDate)
        date.setDate(date.getDate() + i)
        dateArray.push({
          date: date.toISOString().split('T')[0], // YYYY-MM-DD format
          count: 0
        })
      }
      
      // Count searches for each day
      if (searches && searches.length > 0) {
        searches.forEach(search => {
          const searchDate = new Date(search.created_at).toISOString().split('T')[0]
          const dayIndex = dateArray.findIndex(day => day.date === searchDate)
          if (dayIndex !== -1) {
            dateArray[dayIndex].count++
          }
        })
      }
      
      return dateArray
    } catch (error) {
      console.error('Error calculating search trend:', error)
      return Array(7).fill().map((_, i) => ({
        date: new Date(new Date().setDate(new Date().getDate() - (6 - i))).toISOString(),
        count: 0
      }))
    }
  }

  static async generateRelatedKeywords(keywords) {
    try {
      if (!keywords || keywords.length === 0) {
        return []
      }
      
      // Simple approach: extract words and find related combinations
      const allWords = keywords.join(' ').toLowerCase()
        .replace(/[^\w\s]/g, '')
        .split(/\s+/)
        .filter(word => word && word.length > 3) // Filter out short words and empty strings
      
      // Get unique words
      const uniqueWords = [...new Set(allWords)]
      
      // If we have too few words, return them
      if (uniqueWords.length <= 10) {
        return uniqueWords
      }
      
      // Get most frequent words
      const wordFrequency = {}
      allWords.forEach(word => {
        wordFrequency[word] = (wordFrequency[word] || 0) + 1
      })
      
      // Sort words by frequency
      const sortedWords = Object.keys(wordFrequency).sort((a, b) => {
        return wordFrequency[b] - wordFrequency[a]
      })
      
      // Take top 10 words
      const topWords = sortedWords.slice(0, 10)
      return topWords
    } catch (error) {
      console.error('Error generating related keywords:', error)
      return []
    }
  }
}